import {SWRKeyEnum} from 'interfaces';
import swrHookFactory from './swrHookFactory';
import backendCommunicator from 'services/backendCommunicator';
import {type CageResponse, type Region, type Site} from 'client';
import {DEFAULT_POLLING_INTERVAL} from 'appConstants';
import {
  type ActionsProcessedFetcher,
  dateObjectMiddleware,
  datePeriodsMiddleware,
  type DelousingProcessedFetcher,
  type HarvestOverviewProcessedFetcher,
  type HarvestProcessedFetcher,
  type InputProcessedFetcher,
  type LiveFishTransactionProcessedFetcher,
  type SingleDelousingProcessedFetcher,
  type SiteActionsProcessedFetcher,
  sortByNameMiddleware,
  sortSiteCageNaturally,
  type TransferProcessedFetcher,
} from './swrMiddlewares';
import {type LDFlagSet} from 'launchdarkly-react-client-sdk';

export const EMPTY_ID = 'empty_id'; // forces SWR to load resourse if it has no ID

// PLAN

export const useGetPlans = swrHookFactory(
  SWRKeyEnum.GET_PLANS,
  backendCommunicator.getPlans.bind(backendCommunicator),
  {revalidateOnFocus: false},
);

export const useGetPlan = swrHookFactory(
  SWRKeyEnum.GET_PLAN,
  backendCommunicator.getPlanSimple.bind(backendCommunicator),
);

export const useGetPlanCages = swrHookFactory<CageResponse[]>(
  SWRKeyEnum.GET_PLAN_CAGES,
  backendCommunicator.getCages.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetPlanFishTypes = swrHookFactory(
  SWRKeyEnum.GET_PLAN_FISH_TYPES,
  backendCommunicator.getFishTypes.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetSites = swrHookFactory<Site[]>(
  SWRKeyEnum.GET_PLAN_SITES,
  backendCommunicator.getSites.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetPlanGrowthModels = swrHookFactory(
  SWRKeyEnum.GET_PLAN_GROWTH_MODELS,
  backendCommunicator.getPlanGrowthModels.bind(backendCommunicator),
);

export const useGetGrowthModel = swrHookFactory(
  SWRKeyEnum.GET_GROWTH_MODEL,
  backendCommunicator.getGrowthModel.bind(backendCommunicator),
);

export const useGetGrowthModelsTypes = swrHookFactory(
  SWRKeyEnum.GET_GROWTH_MODELS_TYPES,
  backendCommunicator.getGrowthModelsTypes.bind(backendCommunicator),
);

export const useGetSmoltSupplies = swrHookFactory(
  SWRKeyEnum.GET_PLAN_SMOLT_SUPPLY,
  backendCommunicator.getSmoltSupplies.bind(backendCommunicator),
);

export const useGetPlanActiveStatusPolling = swrHookFactory(
  SWRKeyEnum.GET_PLAN_ACTIVE_STATUS_POLLING,
  backendCommunicator.getPlanStatus.bind(backendCommunicator),
  {refreshInterval: DEFAULT_POLLING_INTERVAL, revalidateOnFocus: true},
);

export const useGetPlanOptimizationStatePolling = swrHookFactory(
  SWRKeyEnum.GET_PLAN_OPTIMIZATION_STATE_POLLING,
  backendCommunicator.getPlanOptimizationState.bind(backendCommunicator),
  {refreshInterval: DEFAULT_POLLING_INTERVAL, refreshWhenHidden: true},
);

export const useGetPlanOptimizationState = swrHookFactory(
  SWRKeyEnum.GET_PLAN_OPTIMIZATION_STATE,
  backendCommunicator.getPlanOptimizationState.bind(backendCommunicator),
);

export const useGetOptimizationRuns = swrHookFactory(
  SWRKeyEnum.GET_OPTIMIZATION_RUNS,
  backendCommunicator.getAllOptimizationsRuns.bind(backendCommunicator),
);

export const useGetPlanOptimizationRuns = swrHookFactory(
  SWRKeyEnum.GET_PLAN_OPTIMIZATION_RUNS,
  backendCommunicator.getPlanOptimizationState.bind(backendCommunicator),
);

const useGetPlanHarvests = swrHookFactory(
  SWRKeyEnum.GET_PLAN_HARVESTS,
  backendCommunicator.getPlanHarvests.bind(backendCommunicator) as HarvestProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

export const useGetPlanInputs = swrHookFactory(
  SWRKeyEnum.GET_PLAN_INPUTS,
  backendCommunicator.getPlanInputs.bind(backendCommunicator) as InputProcessedFetcher,
  {use: [datePeriodsMiddleware]},
);

export const useGetPlanOrSmoltSupplyInputs = swrHookFactory(
  SWRKeyEnum.GET_PLAN_OR_SMOLT_SUPPLY_INPUTS,
  backendCommunicator.getPlanOrSmoltSupplyInputs.bind(backendCommunicator) as InputProcessedFetcher,
  {use: [datePeriodsMiddleware]},
);
export const useGetUnAssignedInputsToSmoltSupply = swrHookFactory(
  SWRKeyEnum.GET_UNASSIGNED_INPUTS_TO_SMOLT_SUPPLY,
  backendCommunicator.getUnAssignedInputsToSmoltSupply.bind(backendCommunicator) as InputProcessedFetcher,
  {use: [datePeriodsMiddleware]},
);

export const useGetPlanTreeNavigation = swrHookFactory(
  SWRKeyEnum.GET_PLAN_TREE_NAVIGATION,
  backendCommunicator.getPlanTreeNavigation.bind(backendCommunicator),
  {use: [sortSiteCageNaturally]},
);

export const useGetFolders = swrHookFactory(
  SWRKeyEnum.GET_PLAN_FOLDERS,
  backendCommunicator.getFolders.bind(backendCommunicator),
  {revalidateOnFocus: false},
);

export const useGetPlanSynchronizationDraft = swrHookFactory(
  SWRKeyEnum.GET_PLAN_SYNCHRONIZATION_DRAFT,
  backendCommunicator.getPlanSynchronizationDraft.bind(backendCommunicator),
);

// CAGE

export const useGetCage = swrHookFactory(
  SWRKeyEnum.GET_CAGE,
  backendCommunicator.getCageSimple.bind(backendCommunicator),
);

export const useGetCageFishTypes = swrHookFactory(
  SWRKeyEnum.GET_CAGE_FISH_TYPES,
  backendCommunicator.getFishTypes.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetCageHarvests = swrHookFactory(
  SWRKeyEnum.GET_CAGE_HARVESTS,
  backendCommunicator.getCageHarvests.bind(backendCommunicator),
);

export const useGetCageInputs = swrHookFactory(
  SWRKeyEnum.GET_CAGE_INPUTS,
  backendCommunicator.getCageInputs.bind(backendCommunicator),
);

export const useGetCageDelousings = swrHookFactory(
  SWRKeyEnum.GET_CAGE_DELOUSINGS,
  backendCommunicator.getCageDelousings.bind(backendCommunicator) as DelousingProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

/**
 * This hook factory function can be turned into a regular hook once the feature
 * is rolled out and we delete the ownership flag
 */
export const createUseGetCageLiveFishTransactions = (flags: LDFlagSet) =>
  swrHookFactory(
    SWRKeyEnum.GET_CAGE_LIVE_FISH_TRANSACTIONS,
    backendCommunicator.getCageLiveFishTransactions.bind(backendCommunicator) as LiveFishTransactionProcessedFetcher,
    {use: [dateObjectMiddleware]},
  );

// SITE

export const useGetSite = swrHookFactory(
  SWRKeyEnum.GET_SITE,
  backendCommunicator.getSiteSimple.bind(backendCommunicator),
);

export const useGetSiteCages = swrHookFactory<CageResponse[]>(
  SWRKeyEnum.GET_SITE_CAGES,
  backendCommunicator.getCages.bind(backendCommunicator, undefined),
  {use: [sortByNameMiddleware]},
);

export const useGetSiteFallowings = swrHookFactory(
  SWRKeyEnum.GET_SITE_FALLOWINGS,
  backendCommunicator.getSiteFallowings.bind(backendCommunicator),
);

export const useGetPlanFallowings = swrHookFactory(
  SWRKeyEnum.GET_PLAN_FALLOWINGS,
  backendCommunicator.getPlanFallowings.bind(backendCommunicator),
);

const useGetSiteHarvests = swrHookFactory(
  SWRKeyEnum.GET_SITE_HARVESTS,
  backendCommunicator.getSiteHarvests.bind(backendCommunicator) as HarvestProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

const useGetSiteInputs = swrHookFactory(
  SWRKeyEnum.GET_SITE_INPUTS,
  backendCommunicator.getSiteInputs.bind(backendCommunicator) as InputProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

const useGetSiteDelousing = swrHookFactory(
  SWRKeyEnum.GET_SITE_DELOUSINGS,
  backendCommunicator.getSiteDelousing.bind(backendCommunicator) as DelousingProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

export const useGetDelousing = swrHookFactory(
  SWRKeyEnum.GET_DELOUSING,
  backendCommunicator.getDelousing.bind(backendCommunicator) as SingleDelousingProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

// OTHER

export const useGetTransfer = swrHookFactory(
  SWRKeyEnum.GET_TRANSFER,
  backendCommunicator.getTransfer.bind(backendCommunicator),
);

export const useGetFallowing = swrHookFactory(
  SWRKeyEnum.GET_FALLOWING,
  backendCommunicator.getFallowing.bind(backendCommunicator),
);

export const useGetGlobalSettings = swrHookFactory(
  SWRKeyEnum.GET_GLOBAL_SETTINGS,
  backendCommunicator.getGlobalSettings.bind(backendCommunicator),
);

const useGetPlanTransfers = swrHookFactory(
  SWRKeyEnum.GET_PLAN_TRANSFERS,
  backendCommunicator.getPlanTransfers.bind(backendCommunicator),
);

export const useGetSiteTransfers = swrHookFactory(
  SWRKeyEnum.GET_SITE_TRANSFERS,
  backendCommunicator.getSiteTransfers.bind(backendCommunicator),
);

const useGetTransfersProcessed = swrHookFactory(
  SWRKeyEnum.GET_SITE_TRANSFERS,
  backendCommunicator.getSiteTransfers.bind(backendCommunicator) as TransferProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

export const useGetHarvest = swrHookFactory(
  SWRKeyEnum.GET_HARVEST,
  backendCommunicator.getHarvest.bind(backendCommunicator),
);

export const useGetHarvestQualityDistribution = swrHookFactory(
  SWRKeyEnum.GET_HARVEST_QUALITY_DISTRIBUTION,
  backendCommunicator.getHarvestQualityDistribution.bind(backendCommunicator),
);

export const useGetCageQualityDistributionForecast = swrHookFactory(
  SWRKeyEnum.GET_CAGE_QUALITY_DISTRIBUTION_FORECAST,
  backendCommunicator.getCageQualityDistributionForecast.bind(backendCommunicator),
);
export const useGetInput = swrHookFactory(SWRKeyEnum.GET_INPUT, backendCommunicator.getInput.bind(backendCommunicator));

export const useGetCageGrowthForecast = swrHookFactory(
  SWRKeyEnum.GET_CAGES_GROWTH,
  backendCommunicator.getCageGrowthForecast.bind(backendCommunicator),
);

export const useGetCageMoralityForecast = swrHookFactory(
  SWRKeyEnum.GET_CAGES_MORTALITY,
  backendCommunicator.getCageMortalityForecast.bind(backendCommunicator),
);

export const useGetForecastState = swrHookFactory(
  SWRKeyEnum.GET_CAGE_FORECAST,
  backendCommunicator.getForecastState.bind(backendCommunicator),
);

export const useGetActions = swrHookFactory(
  SWRKeyEnum.GET_PLAN_ACTIONS,
  backendCommunicator.getActions.bind(backendCommunicator) as ActionsProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

export const useGetSiteActions = swrHookFactory(
  SWRKeyEnum.GET_SITE_ACTIONS,
  backendCommunicator.getSiteActions.bind(backendCommunicator) as SiteActionsProcessedFetcher,
  {use: [dateObjectMiddleware]},
);

export const useGetConcessions = swrHookFactory(
  SWRKeyEnum.GET_PLAN_CONCESSIONS,
  backendCommunicator.getConcessions.bind(backendCommunicator),
  {revalidateOnFocus: false},
);

export const useGetConcession = swrHookFactory(
  SWRKeyEnum.GET_PLAN_CONCESSION,
  backendCommunicator.getConcession.bind(backendCommunicator),
  {revalidateOnFocus: false},
);

export const useGetSiteGrowthModelAssignment = swrHookFactory(
  SWRKeyEnum.GET_PLAN_SITE_GROWTH_MODEL_ASSIGNMENT,
  backendCommunicator.getSiteGrowthModelOverrides.bind(backendCommunicator),
);

export const useGetInventoryDataStats = swrHookFactory(
  SWRKeyEnum.GET_INVENTORY_DATA_STATS,
  backendCommunicator.getProductionHistoryStats.bind(backendCommunicator),
);

export const useGetSensorData = swrHookFactory(
  SWRKeyEnum.GET_SENSOR_DATA,
  backendCommunicator.getSensorData.bind(backendCommunicator),
);

export const useGetSmoltSupply = swrHookFactory(
  SWRKeyEnum.GET_SMOLT_SUPPLY,
  backendCommunicator.getSmoltSupply.bind(backendCommunicator),
);

// Sales Price Models
export const useGetSalesPriceModels = swrHookFactory(
  SWRKeyEnum.GET_PRICE_MODELS,
  backendCommunicator.getPriceModels.bind(backendCommunicator),
);

const useGetSalesPriceModel = swrHookFactory(
  SWRKeyEnum.GET_PRICE_MODEL,
  backendCommunicator.getPriceModel.bind(backendCommunicator),
);

export const useGetPriceModelRows = swrHookFactory(
  SWRKeyEnum.GET_PRICE_MODELS_ROWS,
  backendCommunicator.getPriceModelRows.bind(backendCommunicator),
);

const useSalesGetPriceModelType = swrHookFactory(
  SWRKeyEnum.GET_PRICE_MODELS_TYPE,
  backendCommunicator.getPriceModelTypeForModel.bind(backendCommunicator),
);

const useGetSalesPriceModelTypes = swrHookFactory(
  SWRKeyEnum.GET_PRICE_MODELS_TYPES,
  backendCommunicator.getPriceModelTypes.bind(backendCommunicator),
);

export const useGetSalesPriceModelAssignments = swrHookFactory(
  SWRKeyEnum.GET_PRICE_MODELS_ASSIGNMENTS,
  backendCommunicator.getPriceModelAssigments.bind(backendCommunicator),
);

// Quality Grading Models
export const useGetQualityGradingModels = swrHookFactory(
  SWRKeyEnum.GET_QUALITY_GRADINGS,
  backendCommunicator.getQualityGradings.bind(backendCommunicator),
);

const useGetQualityGradingModel = swrHookFactory(
  SWRKeyEnum.GET_QUALITY_GRADING,
  backendCommunicator.getQualityGrading.bind(backendCommunicator),
);

export const useGetQualityGradingModelAssignments = swrHookFactory(
  SWRKeyEnum.GET_QUALITY_MODEL_ASSIGNMENTS,
  backendCommunicator.getQualityGradingModelAssigments.bind(backendCommunicator),
);

const useGetQualityGradings = swrHookFactory(
  SWRKeyEnum.GET_QUALITY_GRADINGS,
  backendCommunicator.getQualityGradings.bind(backendCommunicator),
);

export const useGetQualityGrading = swrHookFactory(
  SWRKeyEnum.GET_QUALITY_GRADING,
  backendCommunicator.getQualityGrading.bind(backendCommunicator),
);

const useGetSiteQualityGradingModelOverviewTable = swrHookFactory(
  SWRKeyEnum.GET_SITE_QUALITY_GRADING_MODELS_OVERVIEW_TABLE,
  backendCommunicator.getSiteQualityGradingModelsOverviewTable.bind(backendCommunicator),
);

export const useGetCageAvailableFishGroupQuality = swrHookFactory(
  SWRKeyEnum.GET_CAGE_QUALITY_GRADING_MODELS_OVERVIEW_TABLE,
  backendCommunicator.cagesAvailableFishGroupQualityGet.bind(backendCommunicator),
);

/**
 * @deprecated - forbidden by security considerations
 */

export const useGetTenant = swrHookFactory(
  SWRKeyEnum.GET_TENANT,
  backendCommunicator.getTenant.bind(backendCommunicator),
);

export const useGetFCRModels = swrHookFactory(
  SWRKeyEnum.GET_FCR_MODELS,
  backendCommunicator.getFCRModels.bind(backendCommunicator),
);

export const useGetFCRModel = swrHookFactory(
  SWRKeyEnum.GET_FCR_MODEL,
  backendCommunicator.getFCRModel.bind(backendCommunicator),
);

export const useGetFCRModelRows = swrHookFactory(
  SWRKeyEnum.GET_FCR_MODEL_ROWS,
  backendCommunicator.getFCRModelRows.bind(backendCommunicator),
);

export const useGetAPIVersion = swrHookFactory(
  SWRKeyEnum.GET_API_VERSION,
  backendCommunicator.getAPIVersion.bind(backendCommunicator),
);

export const useGetPlanHistory = swrHookFactory(
  SWRKeyEnum.GET_PLAN_HISTORY,
  backendCommunicator.getPlanHistory.bind(backendCommunicator),
);

export const useGetHarvestDemands = swrHookFactory(
  SWRKeyEnum.GET_HARVEST_DEMANDS,
  backendCommunicator.getHarvestDemands.bind(backendCommunicator),
);

export const useGetLice = swrHookFactory(SWRKeyEnum.GET_LICE, backendCommunicator.getLice.bind(backendCommunicator));

export const useGetLiveFishTransactions = swrHookFactory(
  SWRKeyEnum.GET_PLAN_LIVE_FISH_TRANSACTIONS,
  backendCommunicator.getLiveFishTransactions.bind(backendCommunicator),
);

export const useGetLiveFishTransaction = swrHookFactory(
  SWRKeyEnum.GET_PLAN_LIVE_FISH_TRANSACTION,
  backendCommunicator.getLiveFishTransaction.bind(backendCommunicator),
);

export const useGetNotifications = swrHookFactory(
  SWRKeyEnum.GET_NOTIFICATIONS,
  backendCommunicator.getNotifications.bind(backendCommunicator),
);

export const useGetForecast = swrHookFactory(
  SWRKeyEnum.GET_SITE_CAGE_FORECAST,
  backendCommunicator.getForecast.bind(backendCommunicator),
);

export const useGetPlanForecast = swrHookFactory(
  SWRKeyEnum.GET_PLAN_FORECAST,
  backendCommunicator.getPlanForecast.bind(backendCommunicator),
);

export const useGetHarvestVolume = swrHookFactory(
  SWRKeyEnum.GET_PLANS_HARVEST_VOLUME,
  backendCommunicator.getHarvestVolume.bind(backendCommunicator),
);

export const useGetHarvestsWithDistribution = swrHookFactory(
  SWRKeyEnum.GET_PLAN_HARVESTS_WITH_DISTRIBUTIONS,
  backendCommunicator.getHarvestsWithDistribution.bind(backendCommunicator) as HarvestOverviewProcessedFetcher,
  {use: [datePeriodsMiddleware]},
);

const useGetInputVolume = swrHookFactory(
  SWRKeyEnum.GET_PLANS_INPUT_VOLUME,
  backendCommunicator.getInputVolume.bind(backendCommunicator),
);

export const useGetMABUtilizationPerConcession = swrHookFactory(
  SWRKeyEnum.GET_MAB_UTILIZATION_PER_CONCESSION,
  backendCommunicator.getMABUtilizationPerConcession.bind(backendCommunicator),
);

export const useGetSiteStatus = swrHookFactory(
  SWRKeyEnum.GET_SITE_STATUS,
  backendCommunicator.getSiteStatus.bind(backendCommunicator),
);

export const useGetHarvestPlants = swrHookFactory(
  SWRKeyEnum.GET_HARVEST_PLANTS,
  backendCommunicator.getHarvestPlants.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetHarvestPlant = swrHookFactory(
  SWRKeyEnum.GET_HARVEST_PLANT,
  backendCommunicator.getHarvestPlant.bind(backendCommunicator),
);

export const useGetHarvestPlantShift = swrHookFactory(
  SWRKeyEnum.GET_HARVEST_PLANT_SHIFT,
  backendCommunicator.getHarvestPlantShift.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetHarvestPlansCapacity = swrHookFactory(
  SWRKeyEnum.GET_HARVESTS_PLANTS_CAPACITY,
  backendCommunicator.getHarvestPlantsCapacity.bind(backendCommunicator),
);

export const useGetHarvestPlantAvailable = swrHookFactory(
  SWRKeyEnum.GET_HARVESTS_PLANTS_AVAILABLE,
  backendCommunicator.getHarvestPlantAvailable.bind(backendCommunicator),
);

const useGetSiteTemperature = swrHookFactory(
  SWRKeyEnum.GET_SITE_TEMPERATURE,
  backendCommunicator.getSiteTemperature.bind(backendCommunicator),
);

export const useGetCageTimeSeries = swrHookFactory(
  SWRKeyEnum.GET_CAGE_TIME_SERIES,
  backendCommunicator.getCageTimeSeries.bind(backendCommunicator),
);

const useGetCageTemperature = swrHookFactory(
  SWRKeyEnum.GET_CAGE_TEMPERATURE,
  backendCommunicator.getCageTemperature.bind(backendCommunicator),
);

export const useGetCagesTemperature = swrHookFactory(
  SWRKeyEnum.GET_CAGES_TEMPERATURE,
  backendCommunicator.getCagesTemperature.bind(backendCommunicator),
);

export const useGetMe = swrHookFactory(SWRKeyEnum.GET_ME, backendCommunicator.getMe.bind(backendCommunicator));

export const useCagesWithUnallocatedBiomass = swrHookFactory(
  SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS,
  backendCommunicator.getCagesWithUnallocatedBiomass.bind(backendCommunicator),
);

export const useGetPlanKPIs = swrHookFactory(
  SWRKeyEnum.GET_PLANS_KPIs,
  backendCommunicator.getPlanKpi.bind(backendCommunicator),
);

export const useGetHarvestReports = swrHookFactory(
  SWRKeyEnum.GET_HARVEST_REPORTS,
  backendCommunicator.getHarvestReports.bind(backendCommunicator),
);

export const useGetRegions = swrHookFactory<Region[]>(
  SWRKeyEnum.GET_PLAN_REGIONS,
  backendCommunicator.getRegions.bind(backendCommunicator),
  {use: [sortByNameMiddleware]},
);

export const useGetPlanSettings = swrHookFactory(
  SWRKeyEnum.GET_PLAN_SETTINGS,
  backendCommunicator.getPlanSettings.bind(backendCommunicator),
);

export const useGetConnections = swrHookFactory(
  SWRKeyEnum.GET_DATA_SOURCE_CONNECTIONS,
  backendCommunicator.getConnections.bind(backendCommunicator),
);

export const useGetConnectionState = swrHookFactory(
  SWRKeyEnum.GET_DATA_SOURCE_CONNECTION_STATE,
  backendCommunicator.checkConnectionState.bind(backendCommunicator),
);

export const useGetMortalityModels = swrHookFactory(
  SWRKeyEnum.GET_PLAN_MORTALITY_MODELS,
  backendCommunicator.getMortalityModels.bind(backendCommunicator),
);

export const useGetMortalityModel = swrHookFactory(
  SWRKeyEnum.GET_PLAN_MORTALITY_MODEL,
  backendCommunicator.getMortalityModel.bind(backendCommunicator),
);

export const useGetMortalityModelAssignments = swrHookFactory(
  SWRKeyEnum.GET_PLAN_MORTALITY_MODEL_ASSIGNMENTS,
  backendCommunicator.getMortalityModelAssignments.bind(backendCommunicator),
);

export const useGetFishGroups = swrHookFactory(
  SWRKeyEnum.GET_PLAN_FISH_GROUPS,
  backendCommunicator.getFishGroups.bind(backendCommunicator),
);

export const useGetFishGroup = swrHookFactory(
  SWRKeyEnum.GET_FISH_GROUP,
  backendCommunicator.getFishGroup.bind(backendCommunicator),
);

export const useGetCompanies = swrHookFactory(
  SWRKeyEnum.GET_PLAN_COMPANIES,
  backendCommunicator.getCompanies.bind(backendCommunicator),
);

export const useGetCompany = swrHookFactory(
  SWRKeyEnum.GET_PLAN_COMPANY,
  backendCommunicator.getCompany.bind(backendCommunicator),
);

export const useGetPlanFishGroupProperties = swrHookFactory(
  SWRKeyEnum.GET_PLAN_FISH_GROUP_PROPERTIES,
  backendCommunicator.getFishGrpupProperties.bind(backendCommunicator),
);

export const useGetSiteConcessions = swrHookFactory(
  SWRKeyEnum.GET_SITE_CONCESSIONS,
  backendCommunicator.getSiteConcessions.bind(backendCommunicator),
);
